import { Pagination } from 'antd';
import React, { CSSProperties, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { handleResponse } from '../../../Helper';
import { graphicApi } from '../../../api';
import { SubPermissions, User } from '../../../model/AccountModel';
import { BadgeListInfo } from '../../../model/BadgeModel';
import { PaginationSetting, ResponseObject } from '../../../model/CommonModel';
import { GraphicDirectoryInfo } from '../../../model/GraphicModel';
import { Logger } from '../../../model/LoggingModel';
import { EditNameModal } from '../../badge';
import { GridDataProps, GridListView, Select } from '../../common';
import styles from './cardView.module.scss';

const noPreviewStyles: CSSProperties = { minHeight: '120px', width: '110px' };
const user: User = getUser();
const canEditBadge: boolean = User.getSubComponentPermission(user, SubPermissions.Badges_Badges_Edit).allowed;

type Props = {
	badgeListInfo: BadgeListInfo[];
	pagination: PaginationSetting;
	setPagination: React.Dispatch<React.SetStateAction<PaginationSetting>>;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	loadData: (paginationProp?: PaginationSetting) => void;
	changeActionButtonState: (state: boolean) => void;
	handleOnOpenBadge: (badgeData: Partial<GraphicDirectoryInfo>) => void;
};

type ExtraData = {
	onClick: () => void;
};

const CardView: React.FC<Props> = ({ badgeListInfo, pagination, setPagination, setLoading, loadData, changeActionButtonState, handleOnOpenBadge }) => {
	const [gridData, setGridData] = useState<GridDataProps<ExtraData>[]>([]);
	const [editBadge, setEditBadge] = useState<BadgeListInfo>(undefined);
	const [showEditNameModal, setShowEditNameModal] = useState<boolean>(false);

	useEffect(() => {
		setGridData(createGridData(badgeListInfo));
	}, [JSON.stringify(badgeListInfo)]);

	const handleOnEdit = (id: number) => {
		const badge = badgeListInfo.find(d => d.Id === id);
		batch(() => {
			setEditBadge(badge);
			setShowEditNameModal(true);
		});
	};

	const handleDeleteOkConfirmation = async (id: number): Promise<boolean> => {
		try {
			const response: ResponseObject = await graphicApi.archiveBadge(id);
			if (!handleResponse(response)) {
				loadData();
				return true;
			}
		} catch (e) {
			Logger.writeErrorLog(`${e.name}: ${e.message}`);
		}

		return false;
	};

	const createCover = (previewImage: string): React.ReactNode => {
		if (previewImage) {
			return <img src={`data:image/bmp;base64,${previewImage}`} alt={'Preview'} width={'110px'} />;
		}
		return <img src={`data:image/bmp;base64,`} alt={'NoPreview'} style={noPreviewStyles} />;
	};

	const createGridData = (data: BadgeListInfo[]): GridDataProps<ExtraData>[] => {
		if (data?.length > 0) {
			return data.map<GridDataProps<ExtraData>>(d => ({
				title: d.Name,
				subTitle: FormatDate(d.LastUpdated, false, null, true),
				id: d.Id,
				cover: createCover(d.PreviewImage),
				extraData: {
					onClick: () => {
						handleOnOpenBadge({ Id: d.Id, Name: d.Name, LastUpdated: d.LastUpdated });
					},
				},
			}));
		}
		return [];
	};

	const onPaginationChanged = (pageNumber: number, pageSize: number) => {
		const newPagination: PaginationSetting = {
			...pagination,
			PageNumber: pageNumber,
			PageSize: pageSize,
		};
		setPagination(newPagination);
		loadData(newPagination);
	};

	const onEditCancel = () => {
		setShowEditNameModal(false);
	};

	const onEditConfirm = () => {
		setShowEditNameModal(false);
		loadData(pagination);
	};

	return (
		<div className={styles.container}>
			<GridListView
				actions={{
					onDelete: handleDeleteOkConfirmation,
					onEdit: handleOnEdit,
				}}
				isUserAdmin={true}
				data={gridData}
				badgeCard={true}
				canDelete={canEditBadge}
				canEdit={canEditBadge}
			/>
			{gridData?.length > 0 && (
				<div className={styles.paginationContainer}>
					<Pagination
						size='small'
						showSizeChanger={false}
						pageSizeOptions={['12']}
						current={pagination.PageNumber}
						pageSize={pagination.PageSize}
						total={pagination.TotalItems}
						onChange={onPaginationChanged}
						showTotal={value => `${value.toLocaleString('en-US')} ${_('Items').toLowerCase()}`}
					/>
					<Select value={'12 / page'} disabled />
				</div>
			)}
			{showEditNameModal && editBadge && <EditNameModal maxLength={50} badge={editBadge} onCancel={onEditCancel} onConfirm={onEditConfirm} />}
		</div>
	);
};

export { CardView };
